import { AxiosError } from 'axios';
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import styled from 'styled-components';

import uploadSuccessImage from '@/assets/refund/check_circle.png';
import Button from '@/components/common/Button';
import FlexWrap from '@/components/common/FlexWrap';
import ExcelTable from '@/components/refund/ExcelTable';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { convertComponentToImage } from '@/util/common';
import { uploadExel } from '@/api/user';
import { IUploadExcelImagePayload } from '@/types/user';
import { updateModal } from '@/store/modules/modal';

function RefundComplete() {
  const { touristIndex, refundIndex } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const excelRef = useRef<MutableRefObject<HTMLDivElement | null>>(null);
  const dispatch = useAppDispatch();

  const uploadExcelImage = async () => {
    if (excelRef) {
      const blob = await convertComponentToImage(excelRef.current as any);
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('refundId', refundIndex[0] || refundIndex);
      uploadMutation.mutate({ formData });
    }
  };
  const uploadMutation = useMutation<
    number,
    AxiosError,
    IUploadExcelImagePayload
  >((payload) => uploadExel(payload), {
    retry: false,
  });

  useEffect(() => {
    dispatch(updateModal({ isActive: false, data: null, type: null }));
    const timer = setTimeout(uploadExcelImage, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Container>
      <>
        <Icon src={uploadSuccessImage} />
        <Title>환급 신청 완료</Title>
        <Content>환급을 신청했습니다.</Content>
        <FlexWrap gap='30px'>
          <Button
            text='홈으로'
            width='220px'
            height='52px'
            color='#3A3B3E'
            backgroundColor='#E5E6E8'
            borderRadius='8px'
            onClick={() => navigate('/')}
            fontWeight
          />
          <Button
            text='환급 내역 보기'
            width='220px'
            height='52px'
            color='#FFF'
            backgroundColor='#246CF6'
            borderRadius='8px'
            onClick={() => navigate('/refund-detail')}
            fontWeight
          />
        </FlexWrap>
      </>
      <ExcelContainer ref={excelRef} aria-hidden>
        <ExcelTable
          refundIndex={+refundIndex[0] || +refundIndex}
          touristIndex={+touristIndex}
        />
      </ExcelContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 40px 85px 40px;
  min-height: calc(100vh - 80px);
  overflow-y: auto;
  background-color: ${(props) => props.theme.blueColors.scale3};
`;

const Icon = styled.img`
  width: 80px;
  height: 80px;
`;

const Title = styled.h1`
  margin: 32px 0 8px;
  white-space: pre-wrap;
  text-align: center;
  position: relative;
  font-size: ${(props) => props.theme.fontSize.xLarge};
  font-weight: ${(props) => props.theme.fontWeight.bold};
  line-height: ${(props) => props.theme.lineHeight.xLarge};
`;

const AfterPaymentTitle = styled.h1`
  color: var(--Mono-80, #3a3b3e);

  /* WebHead/28_BD */
  font-family: Pretendard;
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 42px */
  letter-spacing: -0.56px;
`;

const Content = styled.p`
  white-space: pre-wrap;
  text-align: center;
  margin-bottom: 32px;
  color: ${(props) => props.theme.mono[65]};
`;

const ExcelContainer = styled.div<{ ref: any }>`
  position: absolute;
  top: -4000px;
  left: -2000px;
  width: 1200px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const RefundCompleteWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;
export default RefundComplete;
